import * as React from "react";

import { llmApi, type ProvidersResponse } from "@/api/llm";
import {
  settingsApi,
  type TenantSettings as TenantSettingsType,
} from "@/api/settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

export function TenantSettings(): JSX.Element {
  const [settings, setSettings] = React.useState<TenantSettingsType | null>(null);
  const [providers, setProviders] = React.useState<ProvidersResponse | null>(null);
  const [saving, setSaving] = React.useState(false);
  const pushToast = useUIStore((s) => s.pushToast);

  // local form state
  const [provider, setProvider] = React.useState<string>("");
  const [model, setModel] = React.useState<string>("");
  const [budgetUser, setBudgetUser] = React.useState<string>("");
  const [budgetConv, setBudgetConv] = React.useState<string>("");

  React.useEffect(() => {
    void (async () => {
      try {
        const [s, p] = await Promise.all([
          settingsApi.getTenant(),
          llmApi.listProviders(),
        ]);
        setSettings(s);
        setProviders(p);
        setProvider(s.default_llm_provider ?? "");
        setModel(s.default_llm_model ?? "");
        setBudgetUser(s.cost_budget_per_user_daily_usd ?? "");
        setBudgetConv(s.cost_budget_per_conversation_usd ?? "");
      } catch (e) {
        pushToast({
          variant: "destructive",
          title: "Lỗi",
          description: e instanceof Error ? e.message : String(e),
        });
      }
    })();
  }, [pushToast]);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const updated = await settingsApi.updateTenant({
        default_llm_provider: provider || null,
        default_llm_model: model || null,
        cost_budget_per_user_daily_usd: budgetUser ? budgetUser : null,
        cost_budget_per_conversation_usd: budgetConv ? budgetConv : null,
      });
      setSettings(updated);
      pushToast({ variant: "success", title: "Đã lưu cấu hình tenant" });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="text-sm text-muted-foreground">Đang tải...</div>;
  }

  const availableProviders = (providers?.providers ?? []).filter((p) => p.available);
  const selectedProvider = provider
    ? availableProviders.find((p) => p.id === provider)
    : null;

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Cấu hình Tenant</h1>
        <p className="text-sm text-muted-foreground">
          Áp dụng mặc định cho toàn bộ user trong tổ chức (override được bởi
          user setting).
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">LLM mặc định</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Provider</label>
            <select
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value);
                setModel("");
              }}
            >
              <option value="">— Auto (chọn provider available đầu tiên) —</option>
              {availableProviders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {selectedProvider ? (
            <div>
              <label className="block text-sm font-medium mb-1">Model</label>
              <select
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                <option value="">— Auto —</option>
                {selectedProvider.models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                    {m.is_default ? " (khuyến nghị)" : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost guards</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Budget per user / ngày (USD)
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="VD: 5.00"
              value={budgetUser}
              onChange={(e) => setBudgetUser(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Budget per conversation (USD)
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="VD: 0.10"
              value={budgetConv}
              onChange={(e) => setBudgetConv(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? "Đang lưu..." : "Lưu thay đổi"}
        </Button>
      </div>
    </div>
  );
}
